/**
 * Created by Qingger Corp.
 * User: yangzihua
 * Date: 2019/6/6
 * Time: 16:28
 * Desc: 云店设置
 */

import { Component, Vue } from 'vue-property-decorator';
import Axios from 'axios';
import type { ElForm } from 'element-ui/types/form';
import { IPSService } from '@/services/ipsServices';
import imageView from '@/views/components/imgPreview';
import { noop } from '@/util';

// 子组件
import NoAuth from '@/views/components/noAuth/noAuth.vue';
import { Functions } from '@/library/utils/functions';
import baseAPIService from '@/services/base/baseAPIService.ts';
import storageCacheManager from '@/library/cache/storageCacheManager';
import ClassGoods from '@/views/components/pageConfigComponents/classGoods/classGoods';

export enum ModeType {
  UN_CONFIG,
  QIAN_DIAN,
  DAN_DIAN,
  SHOP,
}

@Component({
  components: {
    NoAuth,
  },
})
export default class AppSetting extends Vue {
  /* 数据 */

  /**
   * loading状态
   * @type {boolean}
   */
  public loadingState = false;

  /**
   * app列表
   * @type {Array<SiteGroupInfo>}
   */
  public appList: Array<SiteGroupInfo> = [];

  /**
   * 页码
   * @type {number}
   */
  public page = 1;

  /**
   * 条数
   * @type {number}
   */
  public pageSize = 10;

  /**
   * 总条数
   * @type {number}
   */
  public totalCount = 0;

  /**
   * 是否选中活动
   * @type {boolean}
   */
  public isSelected = false;

  /**
   * 关键字
   * @type {string}
   */
  public keywords = '';

  /**
   * 新建小程序弹窗是否显示
   * @type {boolean}
   */
  public isAddAppPopShow = false;

  public platformVersion = 0;

  public crmAccountList = [];

  /**
   * 添加云店表单
   * @type {ItemObject}
   */
  public appForm: ItemObject = {
    appName: '', // 小程序名称
    operationMode: '',
    actId: '',
  };

  /**
   * 添加云店表单验证规则
   * @type {ItemObject}
   */
  public appFormRules: ItemObject = {
    appName: [
      // 小程序名称
      { required: true, message: '请输入小程序名称', trigger: 'blur' },
      // { min: 2, max: 20, message: "长度在 2 到 20 个字符", trigger: "blur" },
    ],
    operationMode: [{ required: true, message: '请选择运营模式', trigger: 'change' }],
    actId: [{ required: true, message: '请选择所属CRM账套', trigger: 'change' }],
  };

  /**
   * 内容是否显示
   * @type {boolean}
   */
  public isContentBodyShow = false;

  /**
   * 列表入驻网点是否可以进入
   * @type {boolean}
   */
  public isCanToSite = false;

  /**
   * 当前选中的模板
   * @type {string}
   */
  public curTemplate = 'publicMould';

  public corpId = baseAPIService.resolveCorpId();

  /* 方法 */

  /**
   * 关键字搜索
   * @return null
   */
  public onSearchSite(): void {
    this.page = 1;
    this.pageSize = 10;
    this.onGetAppList();
  }

  /**
   * 条数变更
   * @param {number} val
   * @return null
   */
  public onSizeChange(val: number): void {
    this.page = 1;
    this.pageSize = val;
    this.onGetAppList();
  }

  /**
   * 页码变更
   * @param {number} val
   * @return null
   */
  public onPageChange(val: number): void {
    this.page = val;
    this.onGetAppList();
  }

  /**
   * 获取小程序列表
   * @return null
   */
  public onGetAppList(): void {
    this.appList = [];
    const queryParams = {
      page: this.page,
      pageSize: this.pageSize,
      keywords: this.keywords,
    };
    IPSService.getSiteGroupList({ queryParams }).then((res) => {
      // 写入默认数据
      if (res.list.length > 0) {
        res.list.forEach((item) => {
          item.isHovered = false;
          item.isEditing = false;
          item.templateCode = Functions.emptyVaribleHandler(item.templateCode);
          item.empName = Functions.emptyVaribleHandler(item.empName);
          item.time = Functions.timeFormat(item.updateTime);
          item.wxAppName = Functions.emptyVaribleHandler(item.wxAppName);
          item.siteName = Functions.emptyVaribleHandler(item.siteName);
          item.actName = Functions.emptyVaribleHandler(item.actName);
        });
      }
      this.appList = res.list;
      this.totalCount = res.count;
    });
  }

  /**
   * 添加云店
   * @return null
   */
  public onAddApp(): void {
    this.isAddAppPopShow = true;
  }

  /**
   * 清除小程序缓存
   * @param {number} id
   * @return null
   */
  public onClearCache(id: number) {
    this.$confirm('清除缓存后小程序需要重新请求数据，将会有短暂的访问缓慢的现象，有大量人员在访问时不建议清除缓存，因为可能会导致小程序卡顿时间过长', '提示', {
      confirmButtonText: '清除',
      cancelButtonText: '取消',
      type: 'warning',
    })
      .then((res) => {
        if (res == 'confirm') {
          const postParams = {
            sgId: id,
          };
          IPSService.deleteSiteGroupCache({ postParams }).then((res) => {
            if (res) {
              this.$message.success('缓存清除成功');
            } else {
              this.$message.error('缓存清除失败');
            }
          });
        }
      })
      .catch(() => {});
  }

  /**
   * 删除小程序
   * @param {number} id
   * @return null
   */
  public onDeleteApp(id: number): void {
    this.$confirm('是否删除此云店?', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      closeOnClickModal: false,
      type: 'warning',
    }).then(() => {
      const postParams = {
        sgId: id,
      };
      IPSService.deleteSiteGroup({ postParams }).then((res) => {
        if (res.ret) {
          this.$message.success('删除成功');
          this.page = 1;
          this.pageSize = 10;
          this.onGetAppList();
        } else {
          this.$message.error('删除失败');
        }
      });
    }, noop);
  }

  /**
   * 小程序授权
   * @param {string} groupCode
   * @param {number} sgId
   * @return null
   */
  public onGetAuth(groupCode: string, sgId: number): void {
    Axios.get(`/nips/admin/mini/app/auth?groupCode=${groupCode}&gzId=${sgId}&corpId=${this.corpId}`).then((result) => {
      result && result.data.content.result && window.open(result.data.content.result);
    });
  }

  /**
   * 取消授权
   * @return null
   */
  public onCancelAuth(): void {
    window.open('https://mp.weixin.qq.com/');
  }

  /**
   * 去往入驻网点页面
   * @param {number} sgId
   * @param {string} sgName
   * @return null
   */
  public onGoToAppConfig(sgId: number, sgName: string, mode: number, curPage: string, isMoreSite: number): void {
    this.$router.push(`appSetting/site/${sgId}/${mode}/${curPage}/${isMoreSite}`);
    this.$store.commit('onModifyAppName', sgName);
  }

  /**
   * 添加小程序弹窗隐藏
   * @param {string} formName
   * @return null
   */
  public onAddAppPopHide(formName: string): void {
    this.isAddAppPopShow = false;
    (this.$refs[formName] as ElForm).resetFields();
  }

  /**
   * 提交添加云店的数据
   * @param {string} formName
   * @return null
   */
  public onSaveNewApp(formName: string): void {
    (this.$refs[formName] as ElForm).validate((valid) => {
      if (valid) {
        const postParams = {
          sgTitle: this.appForm.appName,
          sgName: this.appForm.appName,
          sites: [],
          list: [],
          templateCode: this.curTemplate,
          operationMode: this.appForm.operationMode ? this.appForm.operationMode : 1,
          actId: 1,
          isMoreSite: this.appForm.operationMode ? (this.appForm.operationMod === 1 ? 1 : 0) : 1,
        };
        if (this.platformVersion == 2) {
          postParams.actId = this.appForm.actId;
        }
        IPSService.updateSiteGroup({ postParams }).then((res) => {
          if (res.ret) {
            this.$message.success('添加成功');
            this.onAddAppPopHide(formName);
            this.page = 1;
            this.pageSize = 10;
            this.onGetAppList();
          } else {
            this.$message.error('添加失败');
          }
        });
      } else {
        return false;
      }
    });
  }

  /**
   * 编辑app名称
   * @param {ItemObject} item
   * @return null
   */
  public onEditAppName(item: ItemObject): void {
    item.isEditing = true;
  }

  /**
   * 储存app名称
   * @param {ItemObject} item
   * @return null
   */
  public onSaveAppName(item: ItemObject): void {
    item.isEditing = false;
    const temList = [];
    item.siteList.forEach((item) => {
      temList.push(item.v_site_code);
    });
    const postParams = {
      sgId: Number.parseInt(item.sgId),
      sgName: item.sgName,
      sgTitle: item.sgName,
      sites: temList,
      list: temList,
      isMoreSite: item.isMoreSite,
    };
    IPSService.updateSiteGroup({ postParams }).then((res) => {
      if (res.ret) {
        this.$message.success('设置成功');
        this.onGetAppList();
      } else {
        this.$message.error('设置失败');
      }
    });
  }

  /**
   * 鼠标移入app名称
   * @param {ItemObject} item
   * @return null
   */
  public onHoverInAppName(item: ItemObject): void {
    item.isHovered = true;
  }

  /**
   * 鼠标移出app名称
   * @param {ItemObject} item
   * @return null
   */
  public onHoverOutAppName(item: ItemObject): void {
    item.isHovered = false;
  }
  /* 周期 */

  /**
   * 当组建创建时
   */
  public created() {
    // 权限检测
    this.isContentBodyShow = this.$store.state.authMenuCodeList.includes('VSHOP-FUNCTION-BASIC-STORE');
    if (this.isContentBodyShow) {
      this.onGetAppList(); // 获取小程序列表
    }
    // 列表入驻网点是否可以进入
    if (this.$store.state.authMenuCodeList.includes('VSHOP-FUNCTION-BASIC-STORE-SITES')) {
      this.isCanToSite = true;
    }
      this.platformVersion = +localStorage.getItem('platformVersion');
      if (this.platformVersion === 2) {
        IPSService.crmAccountList().then((res: ItemArray) => {
          this.crmAccountList = res;
        });
      }
  }

  /**
   * 预览
   * @param sgId
   */
  public preview(sgId: number) {
    IPSService.ipsPreview({
      postParams: {
        sgId,
      },
    }).then((response) => {
      imageView.open({ img: response.list[0].siteQrcode }).then(noop, noop);
    });
  }
}
